import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'
import type {
  AuditEntry,
  Channel,
  ChannelMember,
  ChatLink,
  Overlay,
  TemplateInfo,
  User,
} from '../types'

export function useMe() {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api<User>('/api/auth/me'),
  })
}

export function useChannels() {
  return useQuery({
    queryKey: ['channels'],
    queryFn: () => api<Channel[]>('/api/channels'),
  })
}

export function useChannel(id: string | undefined) {
  return useQuery({
    queryKey: ['channels', id],
    queryFn: () => api<Channel>(`/api/channels/${id}`),
    enabled: !!id,
  })
}

export function useChannelOverlays(id: string | undefined) {
  return useQuery({
    queryKey: ['channels', id, 'overlays'],
    queryFn: async () => {
      const data = await api<Overlay[] | null>(`/api/channels/${id}/overlays`)
      // Backend nil slice'ı JSON'da 'null' yazar — defensive empty array.
      return data ?? []
    },
    enabled: !!id,
  })
}

export function useTemplates() {
  return useQuery({
    queryKey: ['templates'],
    queryFn: () => api<TemplateInfo[]>('/api/templates'),
    staleTime: 5 * 60_000,
  })
}

export function useKickEnabled() {
  return useQuery({
    queryKey: ['auth', 'kick-enabled'],
    queryFn: () => api<{ enabled: boolean }>('/api/auth/kick/enabled'),
    staleTime: Infinity,
  })
}

// === Members ===

export function useChannelMembers(channelId: string | undefined) {
  return useQuery({
    queryKey: ['channels', channelId, 'members'],
    queryFn: () => api<ChannelMember[]>(`/api/channels/${channelId}/members`),
    enabled: !!channelId,
  })
}

export function useAddMember(channelId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (email: string) =>
      api(`/api/channels/${channelId}/members`, {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels', channelId, 'members'] }),
  })
}

export function useRemoveMember(channelId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) =>
      api(`/api/channels/${channelId}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels', channelId, 'members'] }),
  })
}

// === Channel mutations ===

type ChannelPatch = Partial<{
  bot_enabled: boolean
  bot_reply: boolean
  link_tracking_enabled: boolean
  link_tracking_auto_managed: boolean
  link_dup_reply_enabled: boolean
  highlight_authors_enabled: boolean
  highlighted_twitter_authors: string[]
  tracked_twitter_handles: string[]
}>

export function useUpdateChannel(channelId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: ChannelPatch) =>
      api(`/api/channels/${channelId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels', channelId] }),
  })
}

export function useDeleteChannel(channelId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api(`/api/channels/${channelId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels'] }),
  })
}

export function useLeaveChannel(channelId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api(`/api/channels/${channelId}/leave`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels'] }),
  })
}

// === Overlay mutations ===

type OverlayPatch = Partial<{
  name: string
  title: string
  is_active: boolean
}>

export function useUpdateOverlay(channelId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: OverlayPatch }) =>
      api(`/api/overlays/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels', channelId, 'overlays'] }),
  })
}

export function useDeleteOverlay(channelId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api(`/api/overlays/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels', channelId, 'overlays'] }),
  })
}

// === Audit log ===

export function useAuditLog(channelId: string | undefined) {
  return useQuery({
    queryKey: ['channels', channelId, 'audit'],
    queryFn: () => api<AuditEntry[]>(`/api/channels/${channelId}/audit`),
    enabled: !!channelId,
    staleTime: 30_000,
  })
}

// === Links (LinksPage WS hybrid kullanır; bu hook initial fetch için) ===

export function useChannelLinks(channelId: string | undefined) {
  return useQuery({
    queryKey: ['channels', channelId, 'links'],
    queryFn: () => api<{ unread: ChatLink[]; read: ChatLink[] }>(`/api/channels/${channelId}/links`),
    enabled: !!channelId,
    staleTime: 10_000,
  })
}

export function useMarkLinkRead(channelId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (linkId: string) =>
      api(`/api/channels/${channelId}/links/${linkId}/read`, { method: 'POST' }),
    onMutate: (linkId) => {
      qc.setQueryData<{ unread: ChatLink[]; read: ChatLink[] }>(
        ['channels', channelId, 'links'],
        (old) => {
          if (!old) return old
          const link = old.unread.find((l) => l.id === linkId)
          if (!link) return old
          return {
            unread: old.unread.filter((l) => l.id !== linkId),
            read: [{ ...link, read_at: new Date().toISOString() }, ...old.read],
          }
        },
      )
    },
  })
}

export function useDeleteLink(channelId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (linkId: string) =>
      api(`/api/channels/${channelId}/links/${linkId}`, { method: 'DELETE' }),
    onMutate: (linkId) => {
      qc.setQueryData<{ unread: ChatLink[]; read: ChatLink[] }>(
        ['channels', channelId, 'links'],
        (old) =>
          old
            ? {
                unread: old.unread.filter((l) => l.id !== linkId),
                read: old.read.filter((l) => l.id !== linkId),
              }
            : old,
      )
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['channels', channelId, 'links'] }),
  })
}
